import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ApiService } from '../api.service';            // ← path home.component.ts se copy karo
import { DropdownService } from './../dropdown.service';  // ← path home.component.ts se copy karo

interface Member {
  id: number | null;
  name: string;
  role: string;
  location: string;
  image: string | null;
  verified: boolean;
  tier: string;
  bookmarked?: boolean;
  dummy?: boolean;
}

// "Sort By Featured" ke liye tier ki priority
const TIER_RANK: { [key: string]: number } = {
  member_pro_featured: 5,
  business_featured: 4,
  featured: 3,
  verified: 2,
  standard: 1,
};

@Component({
  selector: 'app-directory',
  templateUrl: './directory.component.html',
  styleUrls: ['./directory.component.css']
})
export class DirectoryComponent implements OnInit {
  isNavOpen = false;
  isLoading = false;

  // ============ TOP BAR ============
  searchTerm = new FormControl('');
  sortBy = new FormControl('featured');
  viewMode: 'grid' | 'list' = 'grid';

  // ============ DROPDOWN OPTIONS (dropdownService se) ============
  businessType: string[] = [];
  location: string[] = [];
  state: string[] = [];
  gender: string[] = [];
  race: string[] = [];

  // ============ TIERS ============
  tierOptions = [
    { value: 'standard', label: 'Standard' },
    { value: 'verified', label: 'Verified Member' },
    { value: 'featured', label: 'Featured' },
    { value: 'business_featured', label: 'Business Featured' },
    { value: 'member_pro_featured', label: 'Member Pro Featured' },
  ];

  filterForm: FormGroup;

  // ============ MEMBERS / PAGINATION ============
  allMembers: Member[] = [];
  members: Member[] = [];

  currentPage = 1;
  pageSize = 12;
  totalMembers = 0;
  totalPages = 0;
  visiblePages: number[] = [];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private dropdownService: DropdownService
  ) {
    this.filterForm = this.fb.group(this.defaultFilterValues());
  }

  ngOnInit(): void {
    this.dropdownService.getDropdownOptions().subscribe(data => {
      this.businessType = data.businessType;
      this.location = data.location;
      this.state = data.state;
      this.gender = data.gender;
      this.race = data.race;
    });

    this.sortBy.valueChanges.subscribe(() => this.setPage(1));
    this.loadMembers();
  }

  private defaultFilterValues(): any {
    const tiers: any = {};
    this.tierOptions.forEach(t => (tiers[t.value] = [false]));
    return {
      businessType: [''],
      location: [''],
      city: [''],
      state: [''],
      race: [''],
      gender: [''],
      tiers: this.fb.group(tiers),
    };
  }

  // ============ DATA LOADING ============
  private buildPayload(): any {
    const f = this.filterForm.value;
    return {
      name: (this.searchTerm.value || '').trim(),
      business_type: f.businessType,
      location: f.location,
      state: f.state,
      city: (f.city || '').trim(),
      race: f.race,
      gender: f.gender,
      tiers: this.tierOptions.filter(t => f.tiers[t.value]).map(t => t.value),
    };
  }

  private hasActiveFilters(p: any): boolean {
    return !!(p.name || p.business_type || p.location || p.state ||
              p.city || p.race || p.gender || p.tiers.length);
  }

  loadMembers(): void {
    const payload = this.buildPayload();
    const hasFilters = this.hasActiveFilters(payload);
    this.isLoading = true;

    this.apiService.getDirectoryCards(payload).subscribe(
      (response) => {
        const cards = response.Cards || [];
        if (cards.length > 0) {
          this.allMembers = cards.map((c: any) => this.mapCard(c));
        } else if (!hasFilters) {
          this.allMembers = this.getDummyMembers();  // DB ma koi card hi nahi
        } else {
          this.allMembers = [];                      // filter lagaya, kuch nahi mila
        }
        this.isLoading = false;
        this.setPage(1);
      },
      () => {
        this.allMembers = hasFilters ? [] : this.getDummyMembers();
        this.isLoading = false;
        this.setPage(1);
      }
    );
  }

  private mapCard(c: any): Member {
    const place = [c.city, c.state].filter(Boolean).join(', ');
    return {
      id: c.id,
      name: ((c.firstname || '') + ' ' + (c.lastname || '')).trim(),
      role: c.business_type || '',
      location: place || c.location || '',
      image: c.profileImage || null,
      verified: c.is_verified === 'Yes',
      tier: c.tier || 'standard',
      bookmarked: false,
    };
  }

  private getDummyMembers(): Member[] {
    const base = [
      { name: 'Lady Luxe', role: 'Content Creator | Influencer', location: 'Atlanta, GA', tier: 'featured', verified: true },
      { name: 'Yung Reel', role: 'Music Producer', location: 'Miami, FL', tier: 'verified', verified: true },
      { name: 'Glam Beauty', role: 'Beauty Studio', location: 'Los Angeles, CA', tier: 'business_featured', verified: true },
      { name: 'Fit Focus', role: 'Fitness Trainer', location: 'Dallas, TX', tier: 'member_pro_featured', verified: true },
      { name: 'Boss Moves Agency', role: 'Marketing Agency', location: 'Houston, TX', tier: 'business_featured', verified: false },
      { name: 'Chef Nyla', role: 'Private Chef', location: 'Charlotte, NC', tier: 'standard', verified: false },
      { name: 'Elite Events', role: 'Event Planner', location: 'Atlanta, GA', tier: 'featured', verified: false },
      { name: 'Creative Vision Co.', role: 'Photography', location: 'New York, NY', tier: 'standard', verified: false },
    ];
    return base.map((m, i) => ({
      id: null,
      image: `assets/images/members/member${i + 1}.png`,
      bookmarked: false,
      dummy: true,
      ...m,
    }));
  }

  // ============ TIER HELPERS ============
  showTierBadge(tier: string): boolean {
    return !!tier;
  }

  tierLabel(tier: string): string {
    const found = this.tierOptions.find(t => t.value === tier);
    return found ? found.label : tier.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // ============ SEARCH / FILTER ============
  searchMembers(): void {
    this.loadMembers();
  }

  applyFilters(): void {
    this.loadMembers();
  }

  resetFilters(): void {
    const tiers: any = {};
    this.tierOptions.forEach(t => (tiers[t.value] = false));
    this.filterForm.reset({
      businessType: '', location: '', city: '', state: '',
      race: '', gender: '', tiers,
    });
    this.searchTerm.setValue('');
    this.loadMembers();
  }

  // ============ BOOKMARK ============
  toggleBookmark(member: Member): void {
    member.bookmarked = !member.bookmarked;
    // TODO: yahan API call karo save/remove bookmark ke liye
  }

  // ============ SORT + PAGINATION ============
  private getSortedMembers(): Member[] {
    const list = [...this.allMembers];
    switch (this.sortBy.value) {
      case 'newest': return list.sort((a, b) => (b.id || 0) - (a.id || 0));
      case 'az':     return list.sort((a, b) => a.name.localeCompare(b.name));
      case 'za':     return list.sort((a, b) => b.name.localeCompare(a.name));
      default:       return list.sort((a, b) => (TIER_RANK[b.tier] || 0) - (TIER_RANK[a.tier] || 0));
    }
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.setPage(page);
  }

  private setPage(page: number): void {
    const sorted = this.getSortedMembers();
    this.totalMembers = sorted.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalMembers / this.pageSize));
    this.currentPage = Math.min(page, this.totalPages);

    const start = (this.currentPage - 1) * this.pageSize;
    this.members = sorted.slice(start, start + this.pageSize);

    this.updateVisiblePages();
  }

  private updateVisiblePages(): void {
    const maxButtons = 3;
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 1);
    const end = Math.min(this.totalPages, start + maxButtons - 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    this.visiblePages = pages;
  }
}